import { Link } from "react-router-dom";
import {
  Instagram,
  Youtube,
  Twitter,
  ArrowRight,
  Sparkles,
  Twitch,
  Play,
} from "lucide-react";
import { FadeIn, Container, SectionDivider } from "@/components/Section";

// SVG TikTok icon — lucide doesn't ship one. Music-note glyph in TOK's amber.
function TikTokIcon({ className = "h-5 w-5" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.91a8.16 8.16 0 0 0 4.77 1.52V6.97a4.85 4.85 0 0 1-1.84-.28Z" />
    </svg>
  );
}

export const TOK_SOCIAL_HANDLES = {
  tiktok: "https://www.tiktok.com/@templeofkulture",
  instagram: "https://www.instagram.com/templeofkulture",
  youtube: "https://www.youtube.com/@templeofkulture",
  twitch: "https://www.twitch.tv/templeofkulture",
  twitter: "https://twitter.com/templeofkulture",
};

export const SOCIAL_LINKS = [
  { key: "tiktok", label: "TikTok", href: TOK_SOCIAL_HANDLES.tiktok, Icon: TikTokIcon },
  { key: "instagram", label: "Instagram", href: TOK_SOCIAL_HANDLES.instagram, Icon: Instagram },
  { key: "youtube", label: "YouTube", href: TOK_SOCIAL_HANDLES.youtube, Icon: Youtube },
  { key: "twitch", label: "Twitch", href: TOK_SOCIAL_HANDLES.twitch, Icon: Twitch },
  { key: "twitter", label: "Twitter / X", href: TOK_SOCIAL_HANDLES.twitter, Icon: Twitter },
];

/**
 * Vertical short-form content funnel.
 * - Surfaces TikTok at the centre (mobile-first audience growth).
 * - Pairs every social touchpoint with a CTA that pulls users deeper
 *   into the TOK ecosystem (signups / experiences / programmes).
 */
export default function SocialFunnel({
  eyebrow = "Follow the world",
  title = "Get TOK in your feed",
  copy = "Short clips from LEVEL UP, projection mapping, immersive shows, anime socials, Ubuntu retreats and behind-the-scenes — then come step inside the real thing.",
  ctaTo = "/join",
  ctaLabel = "Join the Temple",
  withDivider = true,
}) {
  return (
    <>
      {withDivider && <SectionDivider label={eyebrow} />}
      <section
        data-testid="social-funnel"
        className="relative py-20 sm:py-24 lg:py-28 bg-black border-y border-amber-500/10 overflow-hidden"
      >
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <FadeIn className="lg:col-span-7">
              <p className="tok-eyebrow">{eyebrow}</p>
              <h2 className="tok-headline-xl mt-3">
                {title.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-amber-400">
                  {title.split(" ").slice(-1)}
                </span>
              </h2>
              <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-2xl">
                {copy}
              </p>

              {/* Featured TikTok block */}
              <a
                href={TOK_SOCIAL_HANDLES.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-funnel-tiktok-cta"
                className="group mt-10 inline-flex items-center gap-4 border border-amber-400/40 hover:border-amber-400 bg-zinc-950 hover:bg-amber-400/5 transition-colors p-5 max-w-lg w-full"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-amber-400 text-black">
                  <TikTokIcon className="h-6 w-6" />
                </span>
                <span className="flex-1 text-left">
                  <span className="block text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                    Follow on TikTok
                  </span>
                  <span className="block mt-1 font-display font-bold text-lg">
                    @templeofkulture
                  </span>
                  <span className="block mt-1 text-xs text-zinc-400">
                    LEVEL UP clips · projection BTS · Ubuntu atmosphere · anime socials
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 text-amber-400 group-hover:translate-x-1 transition-transform" />
              </a>

              {/* Full social row */}
              <ul className="mt-8 flex flex-wrap gap-2">
                {SOCIAL_LINKS.map(({ key, label, href, Icon }) => (
                  <li key={key}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-funnel-${key}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/10 hover:border-amber-400 hover:text-amber-400 transition-colors text-[11px] uppercase tracking-[0.25em] font-bold text-zinc-300"
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </FadeIn>

            {/* Right column — content funnel CTAs */}
            <FadeIn delay={0.1} className="lg:col-span-5">
              <div className="border border-amber-400/30 bg-zinc-950 p-7 sm:p-8 space-y-5">
                <p className="text-[10px] uppercase tracking-[0.35em] text-amber-400 font-bold">
                  From scroll to stage
                </p>
                <h3 className="font-display font-black text-2xl leading-tight">
                  See it on your phone. <br />
                  <span className="text-amber-400">
                    Live it in our space.
                  </span>
                </h3>
                <ul className="space-y-3">
                  {[
                    { label: "Discover what's on", to: "/whats-on" },
                    { label: "Apply for LEVEL UP", to: "/level-up-xr" },
                    { label: "Book Ubuntu Retreat", to: "/ubuntu-retreat" },
                    { label: "Step inside Windrush", to: "/shows/windrush-immersive-experience" },
                  ].map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        data-testid={`social-funnel-link-${l.to.replace(/[^a-z0-9]+/gi, "-")}`}
                        className="group flex items-center justify-between gap-3 px-4 py-3 border border-white/10 hover:border-amber-400 hover:bg-amber-400/10 transition-colors"
                      >
                        <span className="text-sm font-display font-semibold text-zinc-200 group-hover:text-amber-400">
                          {l.label}
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 text-amber-400 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </li>
                  ))}
                </ul>

                <Link
                  to={ctaTo}
                  data-testid="social-funnel-primary-cta"
                  className="mt-2 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-6 py-4 font-bold uppercase tracking-[0.25em] text-xs transition-colors w-full justify-center"
                >
                  <Sparkles className="h-4 w-4" />
                  {ctaLabel}
                </Link>
              </div>
            </FadeIn>
          </div>
        </Container>
      </section>
    </>
  );
}

export { TikTokIcon };
